import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Film, Loader2, Play, Volume2, MousePointerClick, Eye, RefreshCw } from "lucide-react";

// Compact 3-step funnel widget showing the conversion of the brand-film flow:
// Impressions → Expansions → Full-film clicks, plus unmute rate.
// Reads GET /api/admin/analytics/summary?days=N (admin-only).
const RANGES = [
  { label: "7d", value: 7 },
  { label: "30d", value: 30 },
  { label: "90d", value: 90 },
];

const pct = (num, den) => {
  if (!den || den <= 0) return null;
  return Math.round((num / den) * 1000) / 10; // one decimal
};

const FunnelBar = ({ label, value, max, accent, hint, testid }) => {
  const width = max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 4;
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm font-medium text-[#1A2024]">{label}</span>
        <span className="font-display text-2xl tabular-nums" data-testid={`${testid}-value`}>
          {value.toLocaleString()}
        </span>
      </div>
      <div className="h-2.5 rounded-full bg-[#F3EFE9] overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-500 ease-out"
          style={{ width: `${width}%`, background: accent }}
        />
      </div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
};

const FilmAnalyticsWidget = () => {
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [since, setSince] = useState(null);
  const [error, setError] = useState(null);

  const load = async (d = days) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/analytics/summary", { params: { days: d } });
      setEvents(data.events || []);
      setSince(data.since);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); /* eslint-disable-line */ }, [days]);

  const count = (name) => events.find((e) => e.event === name)?.count || 0;

  const impressions = count("film_chip_impression");
  const expansions = count("film_chip_expanded");
  const dismissals = count("film_chip_dismissed");
  const fullClicks = count("film_chip_full_film_click");
  const chipUnmutes = count("film_chip_unmuted");
  const heroClicks = count("hero_watch_story_click");
  const sectionUnmutes = count("film_section_unmuted");

  const max = Math.max(impressions, expansions, fullClicks, 1);
  const expandRate = pct(expansions, impressions);
  const clickRate = pct(fullClicks, expansions);
  const unmuteRate = pct(chipUnmutes, expansions);
  const dismissRate = pct(dismissals, impressions);

  return (
    <Card className="p-6 bg-white border-black/10 mb-6" data-testid="film-analytics-widget">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#1A2024] text-white flex items-center justify-center">
            <Film className="w-5 h-5" />
          </div>
          <div>
            <div className="label-overline text-[#C05A3C]">Brand film analytics</div>
            <h3 className="font-display text-2xl leading-tight">Floating chip funnel</h3>
            {since && (
              <div className="text-xs text-muted-foreground">
                Since {since.slice(0, 10)} · {events.length} event types tracked
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-[#F3EFE9] rounded-full p-1" data-testid="film-analytics-range">
            {RANGES.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setDays(r.value)}
                data-testid={`film-analytics-range-${r.value}d`}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  days === r.value
                    ? "bg-[#1A2024] text-white shadow"
                    : "text-[#1A2024] hover:bg-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => load(days)}
            disabled={loading}
            data-testid="film-analytics-refresh"
            className="border-[#1A2024] text-[#1A2024]"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {error ? (
        <div className="text-sm text-[#B84A28]" data-testid="film-analytics-error">{error}</div>
      ) : (
        <>
          {/* 3-step funnel */}
          <div className="grid md:grid-cols-3 gap-5" data-testid="film-analytics-funnel">
            <FunnelBar
              label="1. Impressions"
              value={impressions}
              max={max}
              accent="#1A2024"
              hint={`${dismissRate !== null ? `${dismissRate}% dismissed` : "Chip shown to visitors"}`}
              testid="funnel-impressions"
            />
            <FunnelBar
              label="2. Expansions"
              value={expansions}
              max={max}
              accent="#D9923B"
              hint={expandRate !== null ? `${expandRate}% of impressions opened the chip` : "Visitors who opened the chip"}
              testid="funnel-expansions"
            />
            <FunnelBar
              label="3. Full-film clicks"
              value={fullClicks}
              max={max}
              accent="#C05A3C"
              hint={clickRate !== null ? `${clickRate}% of expansions clicked through` : "Clicked through to landing film"}
              testid="funnel-fullclicks"
            />
          </div>

          {/* Secondary stats */}
          <div className="grid sm:grid-cols-3 gap-3 mt-6 pt-5 border-t border-black/5">
            <div className="flex items-center gap-3" data-testid="film-stat-unmute-rate">
              <div className="w-9 h-9 rounded-full bg-[#4A5D23]/15 text-[#4A5D23] flex items-center justify-center">
                <Volume2 className="w-4 h-4" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Chip unmute rate</div>
                <div className="font-display text-xl">
                  {unmuteRate !== null ? `${unmuteRate}%` : "—"}
                </div>
                <div className="text-[11px] text-muted-foreground">{chipUnmutes.toLocaleString()} unmutes / {expansions.toLocaleString()} expansions</div>
              </div>
            </div>
            <div className="flex items-center gap-3" data-testid="film-stat-hero-clicks">
              <div className="w-9 h-9 rounded-full bg-[#C05A3C]/15 text-[#C05A3C] flex items-center justify-center">
                <Play className="w-4 h-4" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Hero &ldquo;Watch story&rdquo;</div>
                <div className="font-display text-xl">{heroClicks.toLocaleString()}</div>
                <div className="text-[11px] text-muted-foreground">Landing CTA clicks</div>
              </div>
            </div>
            <div className="flex items-center gap-3" data-testid="film-stat-section-unmutes">
              <div className="w-9 h-9 rounded-full bg-[#1A2024]/10 text-[#1A2024] flex items-center justify-center">
                <Eye className="w-4 h-4" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Section unmutes</div>
                <div className="font-display text-xl">{sectionUnmutes.toLocaleString()}</div>
                <div className="text-[11px] text-muted-foreground">On landing FilmSection</div>
              </div>
            </div>
          </div>

          {impressions === 0 && expansions === 0 && fullClicks === 0 && !loading && (
            <div className="mt-5 text-xs text-muted-foreground flex items-center gap-2" data-testid="film-analytics-empty">
              <MousePointerClick className="w-3 h-3" />
              No film events recorded yet in this window. Events start logging as soon as the floating chip impresses a visitor.
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default FilmAnalyticsWidget;
